// @ts-strict-ignore
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, useToast } from "@aviary";
import { fetchPostWithJson } from "@helpers/http";
import { ConfirmModal } from "@shared/components/ConfirmModal/ConfirmModal";
import { OAuthAuthorizationScope } from "@unauthenticated/oauth/components/CanadaOAuthOnboardingFlow/OAuthAuthorizationStep/OAuthAuthorizationScope/OAuthAuthorizationScope";
import { OAuthStepHeader } from "@unauthenticated/oauth/components/CanadaOAuthOnboardingFlow/OAuthStepHeader/OAuthStepHeader";
import { useOauthAuthorizationsUnauthenticatedOAuthQuery as useOauthAuthorizationsQuery } from "@unauthenticated/oauth/data/queries/OauthAuthorizationsQuery/OauthAuthorizations.query.generated";
import { l } from "@unauthenticated/oauth/locales/i18n";

import { SkeletonOAuthAuthorizationStep } from "./SkeletonOAuthAuthorizationStep";

import * as styles from "./OAuthAuthorizationStep.styles";

interface Props {
  clientId: string;
  redirectUri: string;
  responseType: string;
  scopes: string;
  state: string;
  applicationName: string;
}

const OAuthAuthorizationStep = ({
  clientId,
  redirectUri,
  responseType,
  scopes,
  state,
  applicationName,
}: Props) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { data, loading } = useOauthAuthorizationsQuery({
    variables: { clientId },
  });

  if (loading) return <SkeletonOAuthAuthorizationStep />;

  const scopeDescriptions = data?.viewer?.oauthApplication?.scopeDescriptions ?? [];

  const handleOnAgree = async () => {
    try {
      const response = await fetchPostWithJson<{ status: string; redirect_uri: string }>(
        "/api/oauth/authorize",
        {
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: responseType || "code",
          scope: scopes,
          state,
        }
      );
      if (response.status !== "redirect") {
        makeToast("error", t(l.oauth.unauthorized));
      } else {
        window.location.assign(response.redirect_uri);
      }
    } catch (error) {
      makeToast("error", error);
    }
  };

  const renderContent = () =>
    scopeDescriptions.map(scopeData => (
      <OAuthAuthorizationScope key={scopeData.title} {...scopeData} />
    ));

  const renderConfirmModal = () => (
    <ConfirmModal
      headingText={t(l.oauth.cancelIntegration.title)}
      bodyText={t(l.oauth.cancelIntegration.description, { applicationName })}
      primaryButtonText={t(l.oauth.cancelIntegration.confirm)}
      primaryOnClick={() => window.location.assign(redirectUri)}
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
    />
  );

  return (
    <div css={styles.main}>
      <OAuthStepHeader
        title={t(l.oauth.authorization.title)}
        description={t(l.oauth.authorization.description)}
      />
      <section>{renderContent()}</section>
      <section css={styles.actions}>
        <Button intention="textSystem" onClick={() => setIsModalOpen(true)}>
          {t(l.oauth.cancel)}
        </Button>
        <Button onClick={handleOnAgree}>{t(l.oauth.IAgree)}</Button>
      </section>
      {renderConfirmModal()}
    </div>
  );
};

export { OAuthAuthorizationStep };
